import React, { useState, useEffect, useRef } from 'react'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import moment from 'moment'
import numeral from 'numeral'
import { useSelector, useDispatch } from 'react-redux'

import { useQuery } from '../../../hooks/useQuery'
import { getInvoicesWorkTimes, getInvoicesExpenses, createInvoice, updateInvoice, getInvoice, getInvoiceSettings } from '../../../api'
import { downloadInvoicePdf, previewInvoicePdfUrl, convertWorkTimeToInvoiceItem, convertExpenseToInvoiceItem, getGroupedItems, buildInvoiceContext, applyMarkUps } from '../../../helpers/invoice'
import { addSnackbar, dismissSnackbar } from '../../../store/organizationList/actions'

import styles from './styles.module.scss'

import { BackIcon, SendIcon, DownloadIcon, MagicIcon, EyeIcon } from '../Icons'
import DRP from '../../Core/DRP/DRP'
import AppNavbar from '../../Core/AppNavbar'
import WorkTimes from '../WorkTimes'
import Expenses from '../Expenses'
import ManualEntries from '../ManualEntries'
import PreviewInvoice from '../PreviewInvoice'
import InvoiceFooter from '../InvoiceFooter'
import InvoiceForm from '../InvoiceForm'

const CreateInvoiceV2 = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { tab: invoiceId } = useQuery(['tab:invoices'])
  const { id: companyId } = useParams()
  const dispatch = useDispatch()

  const companyInfo = useSelector(state => state.company?.companyInfo) || {}

  const formRef = useRef()
  const contentRef = useRef()
  const previewInvoiceRef = useRef()
  const headerRef = useRef()

  const isCreate = invoiceId === 'new_invoice'

  const [invoiceSettings, setInvoiceSettings] = useState({})
  const [activeToolsTab, setActiveToolsTab] = useState('work_times')
  const [{workTimes, expenses, loading}, setData] = useState({workTimes: [], expenses: [], loading: false})
  const [form, setForm] = useState({
    invoice_number: '',
    customer_id: '',
    place_ids: [],
    invoice_date: '',
    due_date: '',
    notes: '',
    internal_notes: '',
    payment_terms: '',
    currency: '',
    language: 'lt',
    default_vat: '0',
    items: [],
    group_by: 'detailed',
    billing_period_start: null,
    billing_period_end : null,
    auto_assign_to_project: false,
    work_times_markup_type: 'percentage',
    work_times_markup_value: 0,
    expenses_markup_type: 'percentage',
    expenses_markup_value: 0,
    discount: 0,
  })

  const {selectedWorkTimes, selectedExpenses, selectedManualEmtries} = form.items.reduce((acc, item) => {
    return {
      ...acc,
      selectedWorkTimes: item.type === 'work_time' ? {...acc.selectedWorkTimes, [item.work_time.id]: item} : acc.selectedWorkTimes,
      selectedExpenses: item.type === 'expense' ? {...acc.selectedExpenses, [item.place_expense.id]: item} : acc.selectedExpenses,
      selectedManualEmtries: item.type === 'manual_entry' ? {...acc.selectedManualEmtries, [item.id]: item} : acc.selectedManualEmtries,
    }
  }, {
    selectedWorkTimes: {},
    selectedExpenses: {},
    selectedManualEmtries: {},
  })

  const options = [{
    title: `${t('Billable work entries')} (${Object.keys(selectedWorkTimes).length})`,
    key: 'work_times',
    badge: 0,
  }, {
    title: `${t('Billable expenses')} (${Object.keys(selectedExpenses).length})`,
    key: 'expenses',
    badge: 0,
  }, {
    title: `${t('Manual items ({{count}}) and Markup', {count: Object.keys(selectedManualEmtries).length})}`,
    key: 'manual_entry',
    badge: 0,
  }]

  useEffect(() => {
    const rect = contentRef.current.getBoundingClientRect()
    const distance = window.innerHeight - rect.top;
    contentRef.current.style.height = `${distance - 20}px`

    // window.addEventListener('resize', () => {
    //   const rect = contentRef.current.getBoundingClientRect()
    //   const distance = window.innerHeight - rect.top;
    //   contentRef.current.style.height = `${distance - 40}px`
    // })

    contentRef.current.addEventListener('scroll', () => {
      const scrollTop = contentRef.current.scrollTop
      if (scrollTop > 16) {
        headerRef.current.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'
      } else {
        headerRef.current.style.boxShadow = 'none'
      }
    })
  }, [])

  useEffect(() => {
    if (isCreate) {
      initNewInvoice(companyId)
    } else {
      initInvoice(companyId, invoiceId)
    }
  }, [invoiceId, companyId])

  useEffect(() => {
    if (form.billing_period_start && form.billing_period_end && form.place_ids.length) {
      getData()
    }
  }, [companyId, form.billing_period_start?.format('YYYY-MM-DD'), form.billing_period_end?.format('YYYY-MM-DD'), form.place_ids.join(',')])

  useEffect(() => {
    if (!form.place_ids.length) {
      setData(prev => ({...prev, workTimes: [], expenses: []}))
      setForm(prev => ({
        ...prev,
        items: prev.items.filter(item => item.type === 'manual_entry'),
      }))
    }
  }, [form.place_ids.length])

  const initNewInvoice = async (companyId) => {
    const invoiceSettings = await getInvoiceSettings(companyId)
    setInvoiceSettings(invoiceSettings)
    formRef.current.setForm({
      currency: invoiceSettings.default_currency,
      notes: invoiceSettings.default_customer_notes || '',
      language: invoiceSettings.default_language,
      default_vat: invoiceSettings.default_vat,
      invoice_number: `${invoiceSettings.invoice_prefix || ''}-${invoiceSettings.next_invoice_number}`,
      payment_terms: invoiceSettings.payment_terms,
      invoice_date: moment().format('YYYY-MM-DD'),
      due_date: moment().add(invoiceSettings.payment_terms, 'days').format('YYYY-MM-DD'),
      auto_assign_to_project: invoiceSettings.auto_assign_to_project,
    })
    setForm(prev => ({
      ...prev,
      currency: invoiceSettings.default_currency,
      default_vat: invoiceSettings.default_vat,
      invoice_number: `${invoiceSettings.invoice_prefix || ''}-${invoiceSettings.next_invoice_number}`,
      billing_period_start: moment().subtract(1, 'month').startOf('month'),
      billing_period_end : moment().subtract(1, 'month').endOf('month'),
      ...(invoiceSettings.default_work_markup ? {
        work_times_markup_type: 'percentage',
        work_times_markup_value: invoiceSettings.default_work_markup,
      } : {}),
      ...(invoiceSettings.default_expense_markup ? {
        expenses_markup_type: 'percentage',
        expenses_markup_value: invoiceSettings.default_expense_markup,
      } : {}),
    }))
    formRef.current.showHelper()
  }

  const initInvoice = async (companyId, invoiceId) => {
    const invoiceSettings = await getInvoiceSettings(companyId)
    setInvoiceSettings(invoiceSettings)
    const invoice = await getInvoice(companyId, invoiceId)
    formRef.current.setForm({
      invoice_number: invoice.invoice_number,
      customer_id: invoice.customer_id,
      place_ids: [...(invoice.with_no_place ? [0] : []), ...invoice.places.map(p => p.id)],
      invoice_date: moment(invoice.invoice_date).format('YYYY-MM-DD'),
      notes: invoice.notes ?? '',
      internal_notes: invoice.internal_notes ?? '',
      payment_terms: invoice.payment_terms,
      currency: invoice.currency,
      language: invoice.language,
      default_vat: invoice.default_vat,
      ...(invoice.due_date ? {due_date: moment(invoice.due_date).format('YYYY-MM-DD')} : {}),
    })
    setForm(prev => ({
      ...prev,
      place_ids: [...(invoice.with_no_place ? [0] : []), ...invoice.places.map(p => p.id)],
      currency: invoice.currency,
      invoice_number: invoice.invoice_number,
      default_vat: invoice.default_vat,
      items: invoice.items,
      group_by: invoice.group_by,
      billing_period_start: invoice.billing_period_start ? moment(invoice.billing_period_start) : prev.billing_period_start,
      billing_period_end: invoice.billing_period_end ? moment(invoice.billing_period_end) : prev.billing_period_end,
      group_by: invoice.group_by,
      auto_assign_to_project: invoice.auto_assign_to_project,
      work_times_markup_type: invoice.work_times_markup_type,
      work_times_markup_value: invoice.work_times_markup_value,
      expenses_markup_type: invoice.expenses_markup_type,
      expenses_markup_value: invoice.expenses_markup_value,
      created_at: invoice.created_at,
      created_by: invoice.created_by,
      updated_at: invoice.updated_at,
      edited_by: invoice.edited_by,
      issued_date: invoice.issued_date,
      issued_by: invoice.issued_by,
      marked_as_paid_date: invoice.marked_as_paid_date,
      marked_as_paid_by: invoice.marked_as_paid_by,
      discount: invoice.discount,
    }))
  }

  const getData = async () => {
    setData(prev => ({...prev, loading: true}))
    const [workTimesRes, expensesRes] = await Promise.all([
      getInvoicesWorkTimes(companyId, {start_date: form.billing_period_start.format('YYYY-MM-DD'), end_date: form.billing_period_end.format('YYYY-MM-DD'), place_ids: form.place_ids}),
      getInvoicesExpenses(companyId, {start_date: form.billing_period_start.format('YYYY-MM-DD'), end_date: form.billing_period_end.format('YYYY-MM-DD'), place_ids: form.place_ids})
    ])
    if (Array.isArray(workTimesRes)) {
      setData(prev => ({...prev, workTimes: workTimesRes, loading: false}))
      const availableWorkTimeIds = new Set(workTimesRes.map(item => String(item.id)))
      setForm(prev => ({
        ...prev,
        items: prev.items.filter(item => {
          return item.type !== 'work_time' || availableWorkTimeIds.has(String(item.work_time.id))
        }),
      }))
    }
    if (Array.isArray(expensesRes)) {
      setData(prev => ({...prev, expenses: expensesRes, loading: false}))
      const availableExpenseIds = new Set(expensesRes.map(item => String(item.id)))
      setForm(prev => ({
        ...prev,
        items: prev.items.filter(item => {
          return item.type !== 'expense' || availableExpenseIds.has(String(item.place_expense.id))
        }),
      }))
    }
  }

  const handleAutoFill = () => {
    setForm(prev => ({
      ...prev,
      items: [
        ...prev.items.filter(item => item.type === 'manual_entry'),
        ...workTimes.filter(wt => !invoiceSettings.lock_entries_on_issue || !['paid', 'sent'].includes(wt.billing_status)).map(wt => convertWorkTimeToInvoiceItem(wt, prev.default_vat)),
        ...expenses.filter(exp => !invoiceSettings.lock_entries_on_issue || !['paid', 'sent'].includes(exp.billing_status)).map(exp => convertExpenseToInvoiceItem(exp, prev.default_vat)),
      ]
    }))
  }

  const handlePreviewInvoice = async () => {
    const invoiceForm = formRef.current.getForm()
    const invoiceData = {
      ...form,
      ...invoiceForm,
      items: applyMarkUps(
        getGroupedItems(form.items, form.group_by),
        {
          work_times_markup_type: form.work_times_markup_type,
          work_times_markup_value: form.work_times_markup_value,
          expenses_markup_type: form.expenses_markup_type,
          expenses_markup_value: form.expenses_markup_value,
        }
      )
    }
    const ctx = buildInvoiceContext(invoiceData, invoiceSettings, companyInfo)
    const url = await previewInvoicePdfUrl(ctx)
    previewInvoiceRef.current.open({ pdfUrl: url, invoice: invoiceData })
  }

  const downloadInvoice = async () => {
    const invoiceForm = formRef.current.getForm()
    const invoiceData = {
      ...form,
      ...invoiceForm,
      items: applyMarkUps(
        getGroupedItems(form.items, form.group_by),
        {
          work_times_markup_type: form.work_times_markup_type,
          work_times_markup_value: form.work_times_markup_value,
          expenses_markup_type: form.expenses_markup_type,
          expenses_markup_value: form.expenses_markup_value,
        }
      )
    }
    const ctx = buildInvoiceContext(invoiceData, invoiceSettings, companyInfo)
    downloadInvoicePdf(ctx)
  }

  const validateItems = (items) => {
    const descriptionExist = items.every(item => Boolean(item.description))
    if (!descriptionExist) {
      dispatch(addSnackbar(t('Description is required in invoice lines'), 'error'))
      setTimeout(() => {dispatch(dismissSnackbar())}, 2000)
      return false
    }
    return true
  }

  const saveInvoice = async (status) => {
    if (formRef.current.validateForm()) {
      if (!validateItems(form.items)) {
        return false
      }
      const total = applyMarkUps(
        form.items,
        {
          work_times_markup_type: form.work_times_markup_type,
          work_times_markup_value: form.work_times_markup_value,
          expenses_markup_type: form.expenses_markup_type,
          expenses_markup_value: form.expenses_markup_value,
        }
      ).reduce((acc, item) => numeral(item.total_price || 0).add(acc).value(), 0)
      const invoiceForm = formRef.current.getForm()
      const invoiceData = {
        ...form,
        ...invoiceForm,
        billing_period_start: form.billing_period_start.format('YYYY-MM-DD'),
        billing_period_end: form.billing_period_end.format('YYYY-MM-DD'),
        status: status,
        total_amount: numeral(total).subtract(form.discount || 0).value(),
        items: form.items.map(({id, ...rest}) => ({
          ...rest,
          ...(rest.type === 'work_time' ? {work_time_id: rest.work_time.id} : {}),
          ...(rest.type === 'expense' ? {place_expense_id: rest.place_expense.id} : {}),
        })),
      }
      const res = await (isCreate ? createInvoice(companyId, invoiceData) : updateInvoice(companyId, invoiceId, invoiceData))
      if (res.errors) {
        if (res.errors.items) {
          dispatch(addSnackbar(t('At least one item is required'), 'error'))
          setTimeout(() => {dispatch(dismissSnackbar())}, 2000)
        }

        if (res.errors.invoice_number) {
          formRef.current.setErrors({invoice_number: res.errors.invoice_number[0]})
        }
        return
      }
      return res
    }
  }

  const handleSaveDraft = async () => {
    const res = await saveInvoice('draft')
    if (res) {
      dispatch(addSnackbar(isCreate ? t('Invoice created successfully') : t('Invoice updated successfully'), 'success'))
      setTimeout(() => {dispatch(dismissSnackbar())}, 2000)
      history.goBack()
    }
  }

  const handleCreateInvoice = async () => {
    const res = await saveInvoice('sent')
    if (res) {
      dispatch(addSnackbar(isCreate ? t('Invoice created successfully') : t('Invoice updated successfully'), 'success'))
      setTimeout(() => {dispatch(dismissSnackbar())}, 2000)
      history.goBack()
      downloadInvoice()
    }
  }

  const handleClearEntries = () => {
    setForm(prev => ({
      ...prev,
      items: [],
    }))
  }

  const handleChangeDate = ({startDate, endDate}) => {
    setForm(prev => ({
      ...prev,
      billing_period_start: moment(startDate),
      billing_period_end: moment(endDate),
    }))
  }

  const handleSelectTab = key => {
    setActiveToolsTab(key)
  }

  const handleSelectWorkTimes = (updatedWorkTimes) => {
    const updatedWorkTimesLines = Object.values(updatedWorkTimes).reduce((acc, workTime) => {
      if (!selectedWorkTimes[workTime.id]) {
        return [
          ...acc,
          convertWorkTimeToInvoiceItem(workTime, form.default_vat)  
        ] 
      }
      return [...acc, selectedWorkTimes[workTime.id]]

    }, [])

    setForm(prev => {
      return {
        ...prev,
        items: [
          ...prev.items.filter(item => item.type !== 'work_time'),
          ...updatedWorkTimesLines,
        ],
      }
    })
  }

  const handleSelectExpenses = (updatedExpenses) => {
    const updatedExpensesLines = Object.values(updatedExpenses).reduce((acc, expense) => {
      if (!selectedExpenses[expense.id]) {
        return [
          ...acc,
          convertExpenseToInvoiceItem(expense, form.default_vat)  
        ] 
      }
      return [...acc, selectedExpenses[expense.id]]

    }, [])

    setForm(prev => {
      return {
        ...prev,
        items: [
          ...prev.items.filter(item => item.type !== 'expense'),
          ...updatedExpensesLines,
        ],
      }
    })
  }

  const handleEditEntry = (updated) => {
    setForm(prev => {
      return {
        ...prev,
        items: prev.items.map(item => item.id === updated.id ? updated : item),
      }
    })
  }

  const groupedItems = applyMarkUps(
    getGroupedItems(form.items, form.group_by),
    {
      work_times_markup_type: form.work_times_markup_type,
      work_times_markup_value: form.work_times_markup_value,
      expenses_markup_type: form.expenses_markup_type,
      expenses_markup_value: form.expenses_markup_value,
    }
  )

  return (
    <div className={styles.container}>
      <div className={styles.header} ref={headerRef}>
        <div className={styles.backButton} onClick={history.goBack}>
          <BackIcon />
        </div>
        <div>
          <div className={styles.headerTitle}>{isCreate ? t('New invoice') : t('Invoice')}</div>
          <div className={styles.invoiceNumber}>{form.invoice_number} &nbsp;</div>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={cn(styles.button, styles.inverted)} onClick={handleAutoFill}>
            <MagicIcon />
            { t('Auto fill') }
          </button>
          <button type="button" className={cn(styles.button, styles.inverted)} onClick={handlePreviewInvoice}>
            <EyeIcon />
            { t('Preview') }
          </button>
          <button type="button" className={cn(styles.button, styles.inverted)} onClick={handleSaveDraft}>
            <DownloadIcon />
            { t('Save as draft') }
          </button>
          <button type="button" className={styles.button} onClick={handleCreateInvoice}>
            <SendIcon />
            { t('Send Invoice') }
          </button>
        </div>
      </div>
      <div className={styles.content} ref={contentRef} style={{paddingBottom: form.items.length ? 64+16 : 16}}>
        <InvoiceForm
          ref={formRef}
          companyId={companyId}
          onChange={updates => setForm(prev => ({
            ...prev,
             ...updates,
          }))}
          showAssignToProject={invoiceSettings.auto_assign_to_project} />
        <div className={styles.tools}>
          <div className={styles.toolsTitle}>{ t('Billing period') }</div>
          <DRP
            right={false}
            style={{top: -280}}
            definedRangesStyle={{top: -300}}
            initRange={{startDate: form.billing_period_start?.toDate(), endDate: form.billing_period_end?.toDate()}}
            onOpen={() => {
              contentRef.current.style.overflow = 'hidden'
            }}
            onClose={() => {
              contentRef.current.style.overflow = 'auto'
            }}
            onChange={handleChangeDate} />
          <AppNavbar
            options={options}
            selected={activeToolsTab}
            onSelect={handleSelectTab} />
        </div>
        {
          (tab => {
            switch (tab) {
              case 'work_times':
                return (
                  <WorkTimes
                    loading={loading}
                    lockEntriesOnIssue={invoiceSettings.lock_entries_on_issue}
                    workTimes={workTimes}
                    selected={selectedWorkTimes}
                    onSelect={handleSelectWorkTimes} />
                )
              case 'expenses':
                return (
                  <Expenses
                    loading={loading}
                    companyId={companyId}
                    lockEntriesOnIssue={invoiceSettings.lock_entries_on_issue}
                    expenses={expenses}
                    selected={selectedExpenses}
                    onSelect={handleSelectExpenses} />
                )
              case 'manual_entry':
                return (
                  <ManualEntries
                    defaultVat={form.default_vat}
                    currency={form.currency}
                    groupBy={form.group_by}
                    discount={form.discount}
                    onChangeGroupBy={({target}) => {
                      setForm(prev => ({...prev, group_by: target.value}))
                    }}
                    markup={{
                      work_times_markup_type: form.work_times_markup_type,
                      work_times_markup_value: form.work_times_markup_value,
                      expenses_markup_type: form.expenses_markup_type,
                      expenses_markup_value: form.expenses_markup_value,
                    }}
                    onChangeMarkup={({ target }) => {
                      const { name, value } = target
                      setForm(prev => ({
                        ...prev,
                        [name]: name.endsWith('_value') ? numeral(value || 0).value() : value,
                      }))
                    }}
                    onChange={(updates) => setForm(prev => ({...prev, ...updates}))}
                    entries={groupedItems}
                    onEditEntry={handleEditEntry}
                    onAddEntry={entry => setForm(prev => ({...form, items: [...prev.items, entry]}))}
                    onRemoveEntry={id => setForm(prev => ({...form, items: prev.items.filter(item => item.id !== id)}))}
                  />
                )
            }
          })(activeToolsTab)
        }
      </div>
      <div className={cn(styles.footer, {[styles.active]: form.items.length})}>
        <InvoiceFooter 
          data={groupedItems}
          vat={form.default_vat}
          discount={form.discount}
          currency={form.currency}
          onClear={handleClearEntries}
          onPreview={handlePreviewInvoice} />
      </div>
      <PreviewInvoice ref={previewInvoiceRef} />
    </div>
  )
}

export default CreateInvoiceV2
